<template>
  <button type="button" class="btn btn-primary btn-sm" @click="showModal()"><font-awesome-icon icon="plus" /> Hinzufügen</button>

  <!-- Create API Key Modal -->
  <div class="modal fade" id="createApiKeyModal" tabindex="-1" aria-labelledby="createApiKeyModal" aria-hidden="true" ref="modalRef">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-primary py-2" data-bs-theme="dark">
          <span class="modal-title text-white my-auto" id="createApiKeyModalLabel">API Key hinzufügen</span>

          <button type="button" class="btn-close btn-sm text-white" @click="closeModal()"></button>
        </div>

        <div class="modal-body" id="createApiKeyModalBody">
          <LoadingSpinner v-if="loading" />

          <div v-else-if="authStore.createdApiKey != null">
            <p>
              Schlüssel:
              <strong>{{ authStore.createdApiKey.name }}</strong>
            </p>

            <p>
              Dies ist der einzige Zeitpunkt, an dem der API Key angezeigt wird. Bitte kopieren Sie den Schlüssel jetzt und bewahren Sie ihn sicher auf. Nach
              dem Schließen dieses Fensters kann der API Key aus Sicherheitsgründen nicht mehr eingesehen werden.
            </p>

            <div class="input-group mb-3">
              <input
                id="createdApiKey"
                type="text"
                class="form-control"
                aria-label="API-Key"
                aria-describedby="copy-key-to-clipboard-btn"
                :value="authStore.createdApiKey.key"
                readonly
              />
              <button class="btn btn-outline-secondary" type="button" id="copy-key-to-clipboard-btn" @click="copyToClipboard()">
                <font-awesome-icon icon="clipboard" />
              </button>
            </div>

            <div class="d-flex justify-content-end mt-5">
              <button type="button" class="btn btn-primary" @click="closeModal()">Schließen</button>
            </div>
          </div>

          <form v-else @submit.prevent="createApiKey">
            <div class="mb-3">
              <label for="name" class="form-label fw-bold">Name</label>
              <input type="text" class="form-control" id="name" v-model="newApiKey.name" />
            </div>

            <div class="mb-3">
              <label for="beschreibung" class="form-label fw-bold">Beschreibung</label>
              <textarea class="form-control" id="beschreibung" rows="3" v-model="newApiKey.description"></textarea>
            </div>

            <div class="mb-3">
              <label for="expiresat" class="form-label fw-bold">Gültigigkeit</label>
              <select id="expiresat" class="form-select" aria-label="Token Gültigkeit" v-model="expiresAt">
                <option :value="null">Für immer</option>
                <option :value="1">1 Monat</option>
                <option :value="2">1 Jahr</option>
                <option :value="3">5 Jahre</option>
              </select>
            </div>

            <hr class="my-3" />

            <div v-if="errorMsg != ''" class="alert alert-danger mb-3">
              Es ist ein Fehler aufgetreten:<br />
              {{ errorMsg }}
            </div>

            <div class="d-flex justify-content-end">
              <button type="button" class="btn btn-secondary me-3" @click="closeModal()">Abbrechen</button>
              <button type="submit" class="btn btn-primary">Speichern</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  //#region Imports
  import { ref, computed } from 'vue';
  import { Modal } from 'bootstrap';

  import { CreateApiKeyRequest } from '@/models/AuthModels';

  import { useCommonStore } from '@/stores/commonStore';
  import { useAuthStore } from '@/stores/authStore';

  import LoadingSpinner from '@/components/LoadingSpinner.vue';
  //#endregion

  const commonStore = useCommonStore();
  const authStore = useAuthStore();

  const loading = computed(() => commonStore.status.createApiKeyLoading);
  const errorMsg = computed(() => commonStore.status.apiKeysErrorMsg);

  const newApiKey = ref(new CreateApiKeyRequest());
  const expiresAt = ref<number | null>(null);

  function createApiKey() {
    newApiKey.value.username = authStore.user.username;

    if (expiresAt.value !== null) {
      const now = new Date();
      now.setHours(23, 59, 59, 0); // Setze die Zeit auf Mitternacht

      switch (expiresAt.value) {
        case 1: // 1 Monat
          now.setMonth(now.getMonth() + 1);
          break;
        case 2: // 1 Jahr
          now.setFullYear(now.getFullYear() + 1);
          break;
        case 3: // 5 Jahre
          now.setFullYear(now.getFullYear() + 5);
          break;
      }

      newApiKey.value.expiresAt = now;
    } else {
      newApiKey.value.expiresAt = undefined; // Für immer gültig
    }

    authStore.createApiKey(newApiKey.value);
  }

  function copyToClipboard() {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(authStore.createdApiKey?.key || '');
      // commonStore.setToastMessage('API Key wurde in die Zwischenablage kopiert.', 'success');
      return;
    }
  }

  //#region Modal
  const modalRef = ref();

  function closeModal() {
    authStore.resetCreatedApiKey();
    expiresAt.value = null;
    Modal.getInstance(modalRef.value)?.hide();
  }

  function showModal() {
    if (modalRef.value) {
      newApiKey.value = new CreateApiKeyRequest();
      new Modal(modalRef.value).show();
    }
  }
  //#endregion
</script>
